import { useEffect, useState } from "react";
import { AppLayout } from "../components/layout/AppLayout";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { api } from "../lib/api";
import type { CompanyProfile, TeamFitTraits } from "../types";

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-navy placeholder:text-slate-300 focus:border-teal focus:outline-none";

const TRAIT_LABELS: { key: keyof TeamFitTraits; label: string }[] = [
  { key: "communication", label: "Communication" },
  { key: "collaboration", label: "Collaboration" },
  { key: "leadership", label: "Leadership" },
  { key: "problemSolving", label: "Problem Solving" },
  { key: "adaptability", label: "Adaptability" },
  { key: "integrity", label: "Integrity" },
];

const DEFAULT_TRAITS: TeamFitTraits = {
  communication: 70,
  collaboration: 70,
  leadership: 60,
  problemSolving: 70,
  adaptability: 70,
  integrity: 70,
};

export default function CompanyCulture() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [workEnvironment, setWorkEnvironment] = useState("");
  const [communicationStyle, setCommunicationStyle] = useState("");
  const [leadershipStyle, setLeadershipStyle] = useState("");
  const [cultureValuesInput, setCultureValuesInput] = useState("");
  const [traits, setTraits] = useState<TeamFitTraits>(DEFAULT_TRAITS);

  useEffect(() => {
    api
      .get<{ company: CompanyProfile | null }>("/api/company/me")
      .then((res) => {
        if (res.company) {
          setName(res.company.name);
          setIndustry(res.company.industry ?? "");
          setWorkEnvironment(res.company.workEnvironment ?? "");
          setCommunicationStyle(res.company.communicationStyle ?? "");
          setLeadershipStyle(res.company.leadershipStyle ?? "");
          setCultureValuesInput(res.company.cultureValues.join(", "));
          setTraits(res.company.cultureTraits);
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load profile."))
      .finally(() => setLoading(false));
  }, []);

  async function save() {
    if (!name.trim()) {
      setError("Company name is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.put("/api/company/me", {
        name,
        industry,
        workEnvironment,
        communicationStyle,
        leadershipStyle,
        cultureValues: cultureValuesInput.split(",").map((s) => s.trim()).filter(Boolean),
        cultureTraits: traits,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <AppLayout title="Team Culture">{null}</AppLayout>;

  return (
    <AppLayout title="Team Culture">
      <Card className="border border-slate-100">
        <Field label="Company Name">
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Industry / Domain">
          <input className={inputClass} value={industry} onChange={(e) => setIndustry(e.target.value)} />
        </Field>
        <Field label="Work Environment">
          <input className={inputClass} value={workEnvironment} onChange={(e) => setWorkEnvironment(e.target.value)} placeholder="Remote / Hybrid" />
        </Field>
        <Field label="Communication Style">
          <input className={inputClass} value={communicationStyle} onChange={(e) => setCommunicationStyle(e.target.value)} placeholder="Open & Horizontal" />
        </Field>
        <Field label="Leadership Style">
          <input className={inputClass} value={leadershipStyle} onChange={(e) => setLeadershipStyle(e.target.value)} placeholder="Supportive Leader" />
        </Field>
        <Field label="Core Team Values (Comma separated)">
          <input className={inputClass} value={cultureValuesInput} onChange={(e) => setCultureValuesInput(e.target.value)} placeholder="Innovation, Collaboration, Growth" />
        </Field>
      </Card>

      <Card className="mt-3 border border-slate-100">
        <p className="mb-2 text-sm font-semibold text-navy">Team Culture Profile (Team-fit Matching Benchmark)</p>
        <div className="space-y-4">
          {TRAIT_LABELS.map(({ key, label }) => (
            <div key={key}>
              <div className="flex items-baseline justify-between">
                <span className="text-xs font-medium text-navy">{label}</span>
                <span className="text-xs text-teal-dark">{traits[key]}</span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                value={traits[key]}
                onChange={(e) => setTraits({ ...traits, [key]: Number(e.target.value) })}
                className="mt-1 w-full accent-teal"
              />
            </div>
          ))}
        </div>
      </Card>

      {error && <p className="mt-2 text-xs text-coral">{error}</p>}
      <Button fullWidth className="mt-3" onClick={save} loading={saving}>
        Save Profile
      </Button>
    </AppLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
      {children}
    </label>
  );
}
